import React, { useCallback, useEffect, useRef, useState } from "react";
import Calendar from "./child/Calendar";
import { Icon } from '@iconify/react';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { toast } from "react-toastify";
import { auth } from "../helper/auth";
import { useAuth } from "../context/AuthContext";
import config from "../config";

// API service for calendar operations
const calendarAPI = {
  // Fetch all events and tasks
  async fetchEvents(userId, filters = {}) {
    const params = new URLSearchParams(filters);
    if (userId) params.set("user_id", userId);
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events?${params}`);
    return response.json();
  },

  // Create new event/task
  async createEvent(eventData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    });
    return response.json();
  },

  async createTask(taskData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/tasks`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(taskData),
    });
    return response.json();
  },

  // Update event
  async updateEvent(id, eventData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(eventData),
    });
    return response.json();
  },

  // Update task
  async updateTask(id, taskData) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/tasks/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(taskData),
    });
    return response.json();
  },

  // Delete event
  async deleteEvent(id) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/events/${id}`, {
      method: "DELETE",
    });
    return response.json();
  },

  async deleteTask(id) {
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/tasks/${id}`, {
      method: "DELETE",
    });
    return response.json();
  },

  // Get task statistics
  async getTaskStats(userId, filters = {}) {
    const params = new URLSearchParams(filters);
    if (userId) params.set("user_id", userId);
    const response = await auth.fetch(`${config.API_BASE_URL}/api/calendar/tasks/stats?${params}`);
    return response.json();
  },
};

const DatePicker = ({ id, name = id, placeholder, defaultValue, required = false }) => {
  const datePickerRef = useRef(null);

  useEffect(() => {
    const picker = flatpickr(datePickerRef.current, {
      enableTime: true,
      dateFormat: "Y-m-d\\TH:i",
      altInput: true,
      altFormat: "j M Y, h:i K",
      defaultDate: defaultValue || undefined,
    });

    return () => picker.destroy();
  }, [defaultValue]);

  return (
    <input
      ref={datePickerRef}
      id={id}
      name={name}
      type="text"
      className="form-control radius-8 bg-base"
      placeholder={placeholder}
      required={required}
    />
  );
};

const toIsoDateTime = (value, fieldName = "Date") => {
  if (!value) throw new Error(`${fieldName} is required`);
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) throw new Error(`${fieldName} is invalid`);
  return parsed.toISOString();
};

const CalendarMainLayer = () => {
  const { user } = useAuth();
  const [events, setEvents] = useState([]);
  const [taskStats, setTaskStats] = useState(null);
  const [selectedEventType, setSelectedEventType] = useState("all"); // 'all', 'event', 'task'
  const [loading, setLoading] = useState(true);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [businessVenues, setBusinessVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState("all");
  const [newEventTitle, setNewEventTitle] = useState("");
  const [newEventDesc, setNewEventDesc] = useState("");
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskDesc, setNewTaskDesc] = useState("");
  const currentUserId = user ? user.id : null;
  const calendarScopeUserId = ["admin", "venue_owner"].includes(user?.role)
    ? null
    : currentUserId;

  useEffect(() => {
    if (user?.role !== "venue_owner") return;

    const fetchBusinessVenues = async () => {
      try {
        const response = await auth.fetch(
          `${config.API_BASE_URL}/api/v2/venues/my-venues`,
        );
        const data = await response.json();
        if (!response.ok) throw new Error(data.message || "Failed to load venues");
        setBusinessVenues(data.venues || []);
      } catch (error) {
        console.error("Error fetching business venues:", error);
        toast.error("Could not load your venues for the calendar");
      }
    };

    fetchBusinessVenues();
  }, [user?.role]);

  // Fetch calendar data
  const fetchCalendarData = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {};
      if (selectedEventType !== "all") filters.event_type = selectedEventType;
      if (selectedVenueId !== "all") filters.venue_id = selectedVenueId;
      const [eventsResponse, statsResponse] = await Promise.all([
        calendarAPI.fetchEvents(calendarScopeUserId, filters),
        calendarAPI.getTaskStats(calendarScopeUserId, {
          ...(selectedVenueId !== "all" ? { venue_id: selectedVenueId } : {}),
        }),
      ]);

      if (eventsResponse.success) {
        setEvents(eventsResponse.data);
      }

      if (statsResponse.success) {
        setTaskStats(statsResponse.data);
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
  }, [selectedEventType, selectedVenueId, calendarScopeUserId]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Helper to safely close modals
  const closeModal = (modalId) => {
    try {
      // 1. Try standard Bootstrap instance method
      const modalEl = document.getElementById(modalId);
      if (modalEl) {
        const modalInstance = window.bootstrap?.Modal?.getInstance(modalEl);
        if (modalInstance) {
          modalInstance.hide();
        } else {
          // 2. Fallback: if instance doesn't exist (e.g. opened via data-attrs),
          // create one just to hide it, or simulate close button click
          const closeBtn = modalEl.querySelector('[data-bs-dismiss="modal"]');
          if (closeBtn) {
            closeBtn.click();
          } else {
            // 3. Last resort: remove classes manually (not recommended but functional)
            modalEl.classList.remove("show");
            modalEl.style.display = "none";
            document.body.classList.remove("modal-open");
            const backdrop = document.querySelector(".modal-backdrop");
            if (backdrop) backdrop.remove();
          }
        }
      }
    } catch (error) {
      console.error(`Error closing modal ${modalId}:`, error);
    }
  };

  // Handle event creation
  const handleCreateEvent = async (eventData) => {
    console.log("=== CREATE EVENT DEBUG START ===");
    console.log("1. Event data received:", eventData);
    console.log("2. User ID:", currentUserId);

    try {
      const fullEventData = {
        ...eventData,
        user_id: currentUserId,
      };
      console.log("3. Full event data to send:", fullEventData);

      const response = eventData.event_type === "task"
        ? await calendarAPI.createTask({
            ...fullEventData,
            ...(fullEventData.task_details || {}),
            task_details: undefined,
          })
        : await calendarAPI.createEvent(fullEventData);
      console.log("4. API response:", response);

      if (response.success) {
        console.log("5. Event created successfully, refreshing data...");
        fetchCalendarData(); // Refresh data

        // Close appropriate modal based on event type
        if (eventData.event_type === "task") {
          closeModal("addTaskModal");
        } else {
          closeModal("exampleModal");
        }

        // Show success message
        toast.success("Created successfully!");

        console.log("=== CREATE EVENT DEBUG END (SUCCESS) ===");
      } else {
        console.error("6. Create failed:", response);
        toast.error(
          "Failed to create event: " + (response.message || "Unknown error")
        );
        console.log("=== CREATE EVENT DEBUG END (FAILURE) ===");
      }
    } catch (error) {
      console.error("7. Error creating event:", error);
      console.log("=== CREATE EVENT DEBUG END (ERROR) ===");
      toast.error("Error creating event: " + error.message);
    }
  };

  // Handle event deletion
  const handleDeleteEvent = async (eventId) => {
    try {
      // Find the event to determine if it's a task
      const eventToDelete = events.find(
        (e) => e.id.toString() === eventId.toString(),
      );

      let response;
      if (eventToDelete?.event_type === "task") {
        // Delete using task endpoint
        response = await calendarAPI.deleteTask(eventToDelete.task_id);
      } else {
        response = await calendarAPI.deleteEvent(eventId);
      }

      const result = response;
      if (result.success) {
        fetchCalendarData();
        closeModal("exampleModalDelete");
        toast.success("Deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      toast.error("Error deleting event");
    }
  };

  // Handle event/task update
  const handleUpdateEvent = async (eventData) => {
    try {
      const isTask = selectedEvent?.event_type === "task";
      console.log("=== UPDATE DEBUG START ===");
      console.log("1. Selected event:", selectedEvent);
      console.log("2. Is task:", isTask);
      console.log("3. Event data to send:", eventData);
      console.log("4. Task ID:", selectedEvent?.task_id);
      console.log("5. Event ID:", selectedEvent?.id);

      let response;

      if (isTask) {
        // Update using task endpoint
        console.log("7. Request body:", JSON.stringify(eventData, null, 2));
        response = await calendarAPI.updateTask(selectedEvent.task_id, eventData);
      } else {
        // Update using event endpoint
        response = await calendarAPI.updateEvent(selectedEvent.id, eventData);
        console.log("Event update response:", response);
      }

      console.log("11. Final response:", response);

      if (response.success) {
        console.log("12. Update successful, refreshing data...");
        fetchCalendarData(); // Refresh data

        // Close appropriate modal
        if (isTask) {
          closeModal("editTaskModal");
        } else {
          closeModal("exampleModalEdit");
        }

        toast.success("Updated successfully!");

        console.log("=== UPDATE DEBUG END (SUCCESS) ===");
      } else {
        console.error("13. Update failed:", response);
        toast.error("Failed to update: " + (response.message || "Unknown error"));
        console.log("=== UPDATE DEBUG END (FAILURE) ===");
      }
    } catch (error) {
      console.error("14. Error updating event:", error);
      console.log("=== UPDATE DEBUG END (ERROR) ===");
      toast.error("Error updating: " + error.message);
    }
  };

  return (
    <>
      <div className="row gy-4">
        <div className="col-xxl-3 col-lg-4">
          <div className="card h-100 p-0">
            <div className="card-body p-24">
              <div className="d-flex align-items-center gap-3 mb-32">
                <button
                  type="button"
                  className="hs-btn-primary text-sm px-12 py-10 flex-grow-1 radius-8 d-flex align-items-center justify-content-center gap-2"
                  data-bs-toggle="modal"
                  data-bs-target="#exampleModal"
                >
                  <Icon
                    icon="solar:add-circle-linear"
                    className="icon text-lg line-height-1"
                  />
                  Add Event
                </button>
                <button
                  type="button"
                  className="hs-btn-primary text-sm px-12 py-10 flex-grow-1 radius-8 d-flex align-items-center justify-content-center gap-2"
                  data-bs-toggle="modal"
                  data-bs-target="#addTaskModal"
                >
                  <Icon
                    icon="solar:add-circle-linear"
                    className="icon text-lg line-height-1"
                  />
                  Add Task
                </button>
              </div>

              {user?.role === "venue_owner" && businessVenues.length > 0 && (
                <div className="mb-24">
                  <label
                    htmlFor="calendarVenueFilter"
                    className="form-label fw-semibold text-primary-light text-sm mb-8"
                  >
                    Venue
                  </label>
                  <select
                    id="calendarVenueFilter"
                    className="form-select radius-8 bg-base"
                    value={selectedVenueId}
                    onChange={(e) => setSelectedVenueId(e.target.value)}
                  >
                    <option value="all">All my venues</option>
                    {businessVenues.map((venue) => (
                      <option key={venue.id} value={venue.id}>
                        {venue.title || venue.business_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Event Type Filter */}
              <div className="mb-24">
                <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                  Filter by Type
                </label>
                <select
                  className="form-select radius-8 bg-base"
                  value={selectedEventType}
                  onChange={(e) => setSelectedEventType(e.target.value)}
                >
                  <option value="all">All Events & Tasks</option>
                  <option value="event">Events Only</option>
                  <option value="task">Tasks Only</option>
                  <option value="meeting">Meetings</option>
                  <option value="reminder">Reminders</option>
                </select>
              </div>

              {/* Task Stats Summary */}
              {taskStats && (
                <div className="mb-32 p-16 bg-neutral-50 radius-8">
                  <h6 className="text-sm fw-semibold mb-12">Task Overview</h6>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="text-secondary-light text-xs">Pending</span>
                    <span className="text-warning-600 fw-medium text-xs">{taskStats.pending_tasks || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between mb-8">
                    <span className="text-secondary-light text-xs">In Progress</span>
                    <span className="text-info-600 fw-medium text-xs">{taskStats.in_progress_tasks || 0}</span>
                  </div>
                  <div className="d-flex justify-content-between">
                    <span className="text-secondary-light text-xs">Completed</span>
                    <span className="text-success-600 fw-medium text-xs">{taskStats.completed_tasks || 0}</span>
                  </div>
                  {(taskStats.cancelled_tasks || 0) > 0 && (
                    <div className="d-flex justify-content-between mt-8">
                      <span className="text-secondary-light text-xs">Cancelled</span>
                      <span className="text-danger-600 fw-medium text-xs">{taskStats.cancelled_tasks}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Dynamic Event List */}
              <div className="mt-32">
                {loading ? (
                  <div className="text-center py-24">
                    <div
                      className="spinner-border spinner-border-sm text-primary-600"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : events.length > 0 ? (
                  events.map((event) => (
                    <div
                      key={event.id}
                      className="event-item d-flex align-items-center justify-content-between gap-4 pb-16 mb-16 border border-start-0 border-end-0 border-top-0"
                    >
                      <div className="flex-grow-1">
                        <div className="d-flex align-items-center gap-10">
                          <span
                            className={`w-12-px h-12-px rounded-circle fw-medium ${
                              event.event_type === "task"
                                ? "bg-success-600"
                                : event.priority === "high"
                                  ? "bg-danger-600"
                                  : event.priority === "urgent"
                                    ? "bg-danger-800"
                                    : event.priority === "low"
                                      ? "bg-info-600"
                                      : "bg-warning-600"
                            }`}
                          />
                          <span className="text-secondary-light text-xs">
                            {new Date(event.start_date).toLocaleDateString(
                              "en-GB",
                              {
                                day: "numeric",
                                month: "short",
                                year: "numeric",
                              },
                            )}
                            {", "}
                            {new Date(event.start_date).toLocaleTimeString(
                              "en-US",
                              {
                                hour: "numeric",
                                minute: "2-digit",
                                hour12: true,
                              },
                            )}
                            {event.end_date &&
                              ` - ${new Date(event.end_date).toLocaleDateString(
                                "en-GB",
                                {
                                  day: "numeric",
                                  month: "short",
                                  year: "numeric",
                                },
                              )}, ${new Date(event.end_date).toLocaleTimeString(
                                "en-US",
                                {
                                  hour: "numeric",
                                  minute: "2-digit",
                                  hour12: true,
                                },
                              )}`}
                          </span>
                          {event.event_type === "task" && (
                            <span className="badge bg-success-100 text-success-600 text-xs px-8 py-2 radius-4">
                              Task
                            </span>
                          )}
                          {event.event_type === "meeting" && (
                            <span className="badge bg-primary-100 text-primary-600 text-xs px-8 py-2 radius-4">
                              Meeting
                            </span>
                          )}
                        </div>
                        <span className="text-primary-light fw-semibold text-sm mt-4 d-block">
                          {event.title}
                        </span>
                        {event.venue_name && (
                          <span className="text-secondary-light text-xs mt-2 d-block">
                            <Icon icon="solar:buildings-2-linear" className="me-1" />
                            {event.venue_name}
                          </span>
                        )}
                        {event.event_type === "task" &&
                          event.progress_percentage !== undefined && (
                            <div className="mt-8">
                              <div className="d-flex justify-content-between align-items-center mb-2">
                                <span className="text-secondary-light text-xs">
                                  Progress:
                                </span>
                                <span className="text-primary-light fw-semibold text-xs">
                                  {event.progress_percentage}%
                                </span>
                              </div>
                              <div
                                className="progress"
                                style={{
                                  height: "12px",
                                  borderRadius: "6px",
                                  backgroundColor: "#e5e7eb",
                                }}
                              >
                                <div
                                  className="progress-bar bg-success-600"
                                  style={{
                                    width: `${event.progress_percentage}%`,
                                    borderRadius: "6px",
                                  }}
                                />
                              </div>
                            </div>
                          )}
                      </div>
                      <div className="dropdown">
                        <button
                          type="button"
                          data-bs-toggle="dropdown"
                          aria-expanded="false"
                        >
                          <Icon
                            icon="entypo:dots-three-vertical"
                            className="icon text-secondary-light"
                          />
                        </button>
                        <ul className="dropdown-menu p-12 border bg-base shadow">
                          <li>
                            <button
                              type="button"
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                              data-bs-toggle="modal"
                              data-bs-target="#exampleModalView"
                              onClick={() => setSelectedEvent(event)}
                            >
                              <Icon
                                icon="hugeicons:view"
                                className="icon text-lg line-height-1"
                              />
                              View
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900 d-flex align-items-center gap-10"
                              data-bs-toggle="modal"
                              data-bs-target={
                                event.event_type === "task"
                                  ? "#editTaskModal"
                                  : "#exampleModalEdit"
                              }
                              onClick={() => setSelectedEvent(event)}
                            >
                              <Icon
                                icon="lucide:edit"
                                className="icon text-lg line-height-1"
                              />
                              Edit
                            </button>
                          </li>
                          <li>
                            <button
                              type="button"
                              className="delete-item dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-danger-100 text-hover-danger-600 d-flex align-items-center gap-10"
                              data-bs-toggle="modal"
                              data-bs-target="#exampleModalDelete"
                              onClick={() => setSelectedEvent(event)}
                            >
                              <Icon
                                icon="fluent:delete-24-regular"
                                className="icon text-lg line-height-1"
                              />
                              Delete
                            </button>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-24">
                    <Icon
                      icon="solar:calendar-linear"
                      className="icon text-3xl text-secondary-light mb-8"
                    />
                    <p className="text-secondary-light text-sm">
                      No{" "}
                      {selectedEventType === "all"
                        ? "events or tasks"
                        : selectedEventType + "s"}{" "}
                      found
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="col-xxl-9 col-lg-8">
          <div className="card h-100 p-0">
            <div className="card-body p-24">
              <div id="wrap">
                <div id="calendar" />
                <div style={{ clear: "both" }} />
                <Calendar
                  events={events}
                  onEventCreate={handleCreateEvent}
                  onEventUpdate={fetchCalendarData}
                  onEventDelete={handleDeleteEvent}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Add Event */}
      <div
        className="modal fade"
        id="exampleModal"
        tabIndex={-1}
        aria-labelledby="exampleModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalLabel">
                Add New Event
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form
                action="#"
                onReset={() => {
                  setNewEventTitle("");
                  setNewEventDesc("");
                }}
              >
                <div className="row">
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="addEventTitle"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Event Title
                    </label>
                    <div className="hs-field-with-counter">
                      <input
                        id="addEventTitle"
                        type="text"
                        className="form-control radius-8"
                        value={newEventTitle}
                        onChange={(e) => setNewEventTitle(e.target.value.slice(0, 100))}
                        maxLength={100}
                        required
                      />
                      <span className="hs-char-counter">{newEventTitle.length}/100</span>
                    </div>
                  </div>
                  {user?.role === "venue_owner" && businessVenues.length > 0 && (
                    <div className="col-12 mb-20">
                      <label
                        htmlFor="addEventVenue"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        Venue
                      </label>
                      <select
                        key={`event-venue-${selectedVenueId}`}
                        id="addEventVenue"
                        className="form-select radius-8 bg-base"
                        defaultValue={selectedVenueId !== "all" ? selectedVenueId : businessVenues[0].id}
                        required
                      >
                        {businessVenues.map((venue) => (
                          <option key={venue.id} value={venue.id}>
                            {venue.title || venue.business_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="startDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Start Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="startDate"
                        placeholder="12 Mar 2026, 10:30 AM"
                        required
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="endDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      End Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="endDate"
                        placeholder="12 Mar 2026, 2:30 PM"
                        required
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="endDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Label
                    </label>
                    <div className="d-flex align-items-center flex-wrap gap-28">
                      <div className="form-check checked-success d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Personal"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Personal"
                        >
                          <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                          Personal
                        </label>
                      </div>
                      <div className="form-check checked-primary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Business"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Business"
                        >
                          <span className="w-8-px h-8-px bg-primary-600 rounded-circle" />
                          Business
                        </label>
                      </div>
                      <div className="form-check checked-warning d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Family"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Family"
                        >
                          <span className="w-8-px h-8-px bg-warning-600 rounded-circle" />
                          Family
                        </label>
                      </div>
                      <div className="form-check checked-secondary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Important"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Important"
                        >
                          <span className="w-8-px h-8-px bg-lilac-600 rounded-circle" />
                          Important
                        </label>
                      </div>
                      <div className="form-check checked-danger d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="Holiday"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="Holiday"
                        >
                          <span className="w-8-px h-8-px bg-danger-600 rounded-circle" />
                          Holiday
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="desc"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Description
                    </label>
                    <div className="hs-field-with-counter">
                      <textarea
                        className="form-control radius-8"
                        id="desc"
                        rows={4}
                        cols={50}
                        value={newEventDesc}
                        onChange={(e) => setNewEventDesc(e.target.value.slice(0, 500))}
                        maxLength={500}
                      />
                      <span className="hs-char-counter">{newEventDesc.length}/500</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button
                      type="reset"
                      className="hs-btn-secondary text-md px-32 py-10 radius-8"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="hs-btn-primary text-md px-32 py-10 radius-8"
                      onClick={(e) => {
                        // Date conversion function
                        const formatDateForDB = (dateString) =>
                          toIsoDateTime(dateString, "Event date");

                        // Collect form data
                        const form = e.target.closest("form");
                        if (!form.reportValidity()) return;
                        const formData = {
                          title: form.querySelector("#addEventTitle").value,
                          start_date: formatDateForDB(
                            form.querySelector("#startDate").value,
                          ),
                          end_date: formatDateForDB(
                            form.querySelector("#endDate").value,
                          ),
                          event_type: "event",
                          description: form.querySelector("#desc").value,
                          venue_id:
                            Number(form.querySelector("#addEventVenue")?.value) || null,
                          color: (() => {
                            const selectedLabel = form.querySelector(
                              'input[name="label"]:checked',
                            );
                            if (!selectedLabel) return "#0789d5";
                            const labelMap = {
                              Personal: "#10b981",
                              Business: "#0789d5",
                              Family: "#f59e0b",
                              Important: "#8b5cf6",
                              Holiday: "#ef4444",
                            };
                            return labelMap[selectedLabel.id] || "#0789d5";
                          })(),
                          priority: "medium",
                        };

                        // Call create handler
                        handleCreateEvent(formData);
                      }}
                    >
                      Add Event
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Add Task */}
      <div
        className="modal fade"
        id="addTaskModal"
        tabIndex={-1}
        aria-labelledby="addTaskModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="addTaskModalLabel">
                Add New Task
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form
                onReset={() => {
                  setNewTaskTitle("");
                  setNewTaskDesc("");
                }}
                onSubmit={(e) => {
                  e.preventDefault();
                  try {
                    const formData = new FormData(e.target);
                    const dueDate = toIsoDateTime(
                      formData.get("taskDueDate"),
                      "Due date",
                    );
                    const taskData = {
                      title: formData.get("taskTitle"),
                      description: formData.get("taskDescription"),
                      start_date: new Date().toISOString(),
                      end_date: dueDate,
                      event_type: "task",
                      priority: formData.get("taskPriority"),
                      venue_id: Number(formData.get("taskVenueId")) || null,
                      task_details: {
                        assigned_to: currentUserId,
                        due_date: dueDate,
                        progress_percentage: 0,
                        task_category: formData.get("taskCategory"),
                        estimated_hours:
                          parseFloat(formData.get("estimatedHours")) || null,
                      },
                    };
                    handleCreateEvent(taskData);
                  } catch (error) {
                    toast.error(error.message);
                  }
                }}
              >
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Task Title
                    </label>
                    <div className="hs-field-with-counter">
                      <input
                        type="text"
                        name="taskTitle"
                        className="form-control radius-8"
                        value={newTaskTitle}
                        onChange={(e) => setNewTaskTitle(e.target.value.slice(0, 100))}
                        maxLength={100}
                        required
                      />
                      <span className="hs-char-counter">{newTaskTitle.length}/100</span>
                    </div>
                  </div>
                  {user?.role === "venue_owner" && businessVenues.length > 0 && (
                    <div className="col-md-6 mb-20">
                      <label
                        htmlFor="taskVenueId"
                        className="form-label fw-semibold text-primary-light text-sm mb-8"
                      >
                        Venue
                      </label>
                      <select
                        key={`task-venue-${selectedVenueId}`}
                        id="taskVenueId"
                        name="taskVenueId"
                        className="form-select radius-8 bg-base"
                        defaultValue={selectedVenueId !== "all" ? selectedVenueId : businessVenues[0].id}
                        required
                      >
                        {businessVenues.map((venue) => (
                          <option key={venue.id} value={venue.id}>
                            {venue.title || venue.business_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Due Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="taskDueDate"
                        placeholder="12 Mar 2026, 5:00 PM"
                        required
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Priority
                    </label>
                    <select
                      name="taskPriority"
                      className="form-select radius-8 bg-base"
                      defaultValue="medium"
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Category
                    </label>
                    <input
                      type="text"
                      name="taskCategory"
                      className="form-control radius-8"
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Estimated Hours
                    </label>
                    <input
                      type="number"
                      name="estimatedHours"
                      className="form-control radius-8"
                      step="0.5"
                      min="0"
                    />
                  </div>
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Description
                    </label>
                    <div className="hs-field-with-counter">
                      <textarea
                        name="taskDescription"
                        className="form-control radius-8"
                        rows={4}
                        cols={50}
                        value={newTaskDesc}
                        onChange={(e) => setNewTaskDesc(e.target.value.slice(0, 500))}
                        maxLength={500}
                      />
                      <span className="hs-char-counter">{newTaskDesc.length}/500</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button
                      type="reset"
                      className="hs-btn-secondary text-md px-32 py-10 radius-8"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="hs-btn-primary text-md px-32 py-10 radius-8"
                    >
                      Add Task
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Modal View Event */}
      <div
        className="modal fade"
        id="exampleModalView"
        tabIndex={-1}
        aria-labelledby="exampleModalViewLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalViewLabel">
                View Details
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Title
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  Design Conference
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Start Date
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  25 Jan 2026, 10:30 AM
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  End Date
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  25 Jan 2026, 2:30 PM
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Description
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4">
                  N/A
                </h6>
              </div>
              <div className="mb-12">
                <span className="text-secondary-light txt-sm fw-medium">
                  Label
                </span>
                <h6 className="text-primary-light fw-semibold text-md mb-0 mt-4 d-flex align-items-center gap-2">
                  <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                  Business
                </h6>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Edit Event */}
      <div
        className="modal fade"
        id="exampleModalEdit"
        tabIndex={-1}
        aria-labelledby="exampleModalEditLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="exampleModalEditLabel">
                Edit Event
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form key={selectedEvent?.id || "edit-event"} action="#">
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Event Title
                    </label>
                    <input
                      id="editEventTitle"
                      type="text"
                      className="form-control radius-8"
                      maxLength={100}
                      defaultValue={selectedEvent?.title || ""}
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editStartDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Start Date
                    </label>
                    <div className=" position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="editStartDate"
                        placeholder="12 Mar 2026, 10:30 AM"
                        defaultValue={selectedEvent?.start_date}
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editEndDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      End Date
                    </label>
                    <div className=" position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="editEndDate"
                        placeholder="12 Mar 2026, 2:30 PM"
                        defaultValue={selectedEvent?.end_date}
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        />
                      </span>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Label
                    </label>
                    <div className="d-flex align-items-center flex-wrap gap-28">
                      <div className="form-check checked-success d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editPersonal"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editPersonal"
                        >
                          <span className="w-8-px h-8-px bg-success-600 rounded-circle" />
                          Personal
                        </label>
                      </div>
                      <div className="form-check checked-primary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editBusiness"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editBusiness"
                        >
                          <span className="w-8-px h-8-px bg-primary-600 rounded-circle" />
                          Business
                        </label>
                      </div>
                      <div className="form-check checked-warning d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editFamily"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editFamily"
                        >
                          <span className="w-8-px h-8-px bg-warning-600 rounded-circle" />
                          Family
                        </label>
                      </div>
                      <div className="form-check checked-secondary d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editImportant"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editImportant"
                        >
                          <span className="w-8-px h-8-px bg-lilac-600 rounded-circle" />
                          Important
                        </label>
                      </div>
                      <div className="form-check checked-danger d-flex align-items-center gap-2">
                        <input
                          className="form-check-input"
                          type="radio"
                          name="label"
                          id="editHoliday"
                        />
                        <label
                          className="form-check-label line-height-1 fw-medium text-secondary-light text-sm d-flex align-items-center gap-1"
                          htmlFor="editHoliday"
                        >
                          <span className="w-8-px h-8-px bg-danger-600 rounded-circle" />
                          Holiday
                        </label>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mb-20">
                    <label
                      htmlFor="editdesc"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Description
                    </label>
                    <textarea
                      className="form-control radius-8"
                      id="editdesc"
                      rows={4}
                      cols={50}
                      maxLength={500}
                      defaultValue={selectedEvent?.description || ""}
                    />
                  </div>
                  <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                    <button
                      type="reset"
                      className="hs-btn-secondary text-md px-32 py-10 radius-8"
                      data-bs-dismiss="modal"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="hs-btn-primary text-md px-32 py-10 radius-8"
                      onClick={(e) => {
                        // Collect form data
                        const form = e.target.closest("form");
                        const formData = {
                          title: form.querySelector("#editEventTitle").value,
                          start_date: form.querySelector("#editStartDate").value,
                          end_date: form.querySelector("#editEndDate").value,
                          event_type: "event",
                          priority: "medium",
                          description: form.querySelector("#editdesc").value,
                        };

                        handleUpdateEvent(formData);
                      }}
                    >
                      Save Changes
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      {/* Modal Delete Event */}
      <div
        className="modal fade"
        id="exampleModalDelete"
        tabIndex={-1}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-sm modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-body p-24 text-center">
              <span className="mb-16 fs-1 line-height-1 text-danger">
                <Icon icon="fluent:delete-24-regular" className="menu-icon" />
              </span>
              <h6 className="text-lg fw-semibold text-primary-light mb-0">
                Are you sure you want to delete this event?
              </h6>
              <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                <button
                  type="button"
                  className="w-50 hs-btn-secondary text-md px-24 py-10 radius-8"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="w-50 btn btn-danger text-md px-24 py-10 radius-8"
                  onClick={() => {
                    if (selectedEvent) {
                      handleDeleteEvent(selectedEvent.id);
                    }
                  }}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Edit Task Modal */}
      <div
        className="modal fade"
        id="editTaskModal"
        tabIndex={-1}
        aria-labelledby="editTaskModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-lg modal-dialog modal-dialog-centered">
          <div className="modal-content radius-16 bg-base">
            <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
              <h1 className="modal-title fs-5" id="editTaskModalLabel">
                Edit Task
              </h1>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              />
            </div>
            <div className="modal-body p-24">
              <form key={selectedEvent?.task_id || "edit-task"} action="#">
                <div className="row">
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Task Title
                    </label>
                    <input
                      id="editTaskTitleInput"
                      type="text"
                      className="form-control radius-8"
                      maxLength={100}
                      defaultValue={selectedEvent?.title || ""}
                    />
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editTaskStartDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      Start Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="editTaskStartDate"
                        placeholder="12 Mar 2026, 10:30 AM"
                        defaultValue={selectedEvent?.start_date}
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label
                      htmlFor="editTaskEndDate"
                      className="form-label fw-semibold text-primary-light text-sm mb-8"
                    >
                      End Date
                    </label>
                    <div className="position-relative">
                      <DatePicker
                        className="form-control radius-8 bg-base"
                        id="editTaskEndDate"
                        placeholder="12 Mar 2026, 11:30 AM"
                        defaultValue={selectedEvent?.end_date || selectedEvent?.due_date}
                      />
                      <span className="position-absolute end-0 top-50 translate-middle-y me-12 line-height-1">
                        <Icon
                          icon="solar:calendar-linear"
                          className="icon text-lg"
                        ></Icon>
                      </span>
                    </div>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Priority
                    </label>
                    <select
                      className="form-select radius-8 bg-base"
                      defaultValue={selectedEvent?.priority || "medium"}
                    >
                      <option value="low">Low</option>
                      <option value="medium">Medium</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                  <div className="col-md-6 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Progress (%)
                    </label>
                    <input
                      type="number"
                      className="form-control radius-8"
                      min="0"
                      max="100"
                      defaultValue={selectedEvent?.progress_percentage || 0}
                    />
                  </div>
                  <div className="col-12 mb-20">
                    <label className="form-label fw-semibold text-primary-light text-sm mb-8">
                      Description
                    </label>
                    <textarea
                      className="form-control radius-8"
                      rows={3}
                      maxLength={500}
                      defaultValue={selectedEvent?.description || ""}
                    />
                  </div>
                </div>
                <div className="d-flex align-items-center justify-content-center gap-3 mt-24">
                  <button
                    type="reset"
                    className="hs-btn-secondary text-md px-32 py-10 radius-8"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="hs-btn-primary text-md px-32 py-10 radius-8"
                    onClick={(e) => {
                      // Collect form data with better selectors
                      const form = e.target.closest("form");
                      const formData = {
                        title: form.querySelector("#editTaskTitleInput").value,
                        start_date:
                          form.querySelector("#editTaskStartDate").value,
                        end_date: form.querySelector("#editTaskEndDate").value,
                        priority: form.querySelector("select").value,
                        progress_percentage:
                          parseInt(
                            form.querySelector('input[type="number"]').value,
                          ) || 0,
                        description: form.querySelector("textarea").value,
                      };

                      // Call update handler
                      handleUpdateEvent(formData);
                    }}
                  >
                    Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CalendarMainLayer;
